import { useEffect, useMemo, useRef, useState } from "react";
import { canFinishFirstHalf, canFinishStory, CONDITION_TEST_LABELS } from "../lib/experiment";
import type { Condition, ExperimentState, Story } from "../types";
import { RelationMap } from "./RelationMap";

type Props = {
  state: ExperimentState;
  story: Story;
  condition: Condition;
  phase: "first" | "second";
  onPosition: (current: number, furthest: number) => void;
  onTab: (tab: "text" | "map") => void;
  onEvent: (type: string, payload?: Record<string, unknown>) => void;
  onContinue: () => void;
};

export function ReadingScreen({
  state,
  story,
  condition,
  phase,
  onPosition,
  onTab,
  onEvent,
  onContinue,
}: Props) {
  const scroller = useRef<HTMLDivElement>(null);
  const stableTimer = useRef<number | undefined>(undefined);
  const [candidate, setCandidate] = useState(state.currentViewportIndex);
  const blocks = useMemo(
    () => phase === "first" ? story.blocks.slice(0, story.interruptionIndex + 1) : story.blocks,
    [phase, story],
  );

  useEffect(() => {
    const root = scroller.current;
    if (!root || phase !== "second") return;
    requestAnimationFrame(() => {
      const target = root.querySelector<HTMLElement>(`[data-block-index="${story.interruptionIndex}"]`);
      if (target) {
        // Place the last paragraph from the first half just above the detection line.
        // This resumes at the interruption point without revealing the next paragraph.
        root.scrollTop = Math.max(0, target.offsetTop - root.clientHeight * 0.55 + 16);
      }
    });
  }, [phase, story.interruptionIndex]);

  const inspectPosition = () => {
    const root = scroller.current;
    if (!root) return;
    const guideY = root.scrollTop + root.clientHeight * 0.55;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>("[data-block-index]"));
    let next = 0;
    for (const node of nodes) {
      if (node.offsetTop <= guideY) next = Number(node.dataset.blockIndex ?? 0);
    }
    setCandidate(next);
    window.clearTimeout(stableTimer.current);
    stableTimer.current = window.setTimeout(() => {
      const furthest = Math.max(state.furthestReadIndex, next);
      onPosition(next, furthest);
    }, 380);
  };

  useEffect(() => {
    inspectPosition();
    return () => window.clearTimeout(stableTimer.current);
    // The first measurement is intentionally tied to the mounted reading phase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const ready = phase === "first"
    ? canFinishFirstHalf(state, story)
    : canFinishStory(state, story);

  return (
    <main className="experiment-shell reading-shell">
      <div className="reading-heading">
        <div>
          <p className="eyebrow">STORY {state.blockNumber + 1} / 3</p>
          <h1>{story.title}</h1>
          <p>{phase === "first" ? "前半を読んでください" : "続きを最後まで読んでください"}</p>
        </div>
        <div className="reading-meta">
          <span className={`condition-meta condition-${condition.toLowerCase()}`}>
            テスト条件 {CONDITION_TEST_LABELS[condition]}
          </span>
          <span>到達位置 {state.furthestReadIndex + 1} / {story.blocks.length}</span>
          <span>{state.guideVisible ? "案内線 表示中" : "案内線 非表示"}</span>
        </div>
      </div>

      <div className="reader-card">
        <div className="tab-bar" role="tablist" aria-label="本文と人物相関図">
          <button
            className={state.activeTab === "text" ? "active" : ""}
            role="tab"
            aria-selected={state.activeTab === "text"}
            type="button"
            onClick={() => onTab("text")}
          >
            本文
          </button>
          <button
            className={state.activeTab === "map" ? "active" : ""}
            role="tab"
            aria-selected={state.activeTab === "map"}
            type="button"
            onClick={() => onTab("map")}
          >
            人物相関図
          </button>
        </div>

        {state.activeTab === "text" ? (
          <div className="text-pane-wrap">
            {state.guideVisible && (
              <div className="reading-guide" aria-hidden="true">
                <span>この位置までの情報を反映</span>
              </div>
            )}
            <div className="text-pane" ref={scroller} onScroll={inspectPosition} tabIndex={0}>
              <article>
                <p className="story-subtitle">{story.subtitle}</p>
                {blocks.map((block, index) => (
                  <p
                    key={block.id}
                    data-block-index={index}
                    className={candidate === index ? "candidate-block" : ""}
                  >
                    {block.text}
                  </p>
                ))}
                <div className="reading-end-marker">
                  {phase === "first" ? "ここで一度、読書を中断します" : "文章はここまでです"}
                </div>
              </article>
            </div>
          </div>
        ) : (
          <RelationMap
            story={story}
            condition={condition}
            furthestReadIndex={state.furthestReadIndex}
            onInteraction={onEvent}
          />
        )}
      </div>

      <div className="page-actions reading-actions">
        <p>{ready ? "次へ進めます。" : "表示されている文章を最後まで読んでください。"}</p>
        <button className="primary-button" type="button" disabled={!ready} onClick={onContinue}>
          {phase === "first" ? "前半を読み終え、休憩へ" : "読書を終えて内容確認へ"}
        </button>
      </div>
    </main>
  );
}
